/**
 * `CHANGE_DATE_DAILY` step. Flips `wo.service_date` so the subsequent
 * `ASSIGN_DAILY` on the target date passes BE `ValidateNewStopsDaily`.
 * Daily-only by definition — the master variant is `CHANGE_DAY_MASTER`.
 */
import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { RouteId, ServiceDate, WorkOrderId } from 'types/entities/shared/ids'
import type {
  ChangeDateDailyResponse,
  Deps,
  PipelineStep,
} from 'types/modules/routing'

import type { StepResult } from '../types'

const BATCH_SIZE = 200

export interface ChangeDateDailyStepArgs {
  readonly routeId: RouteId
  readonly fromServiceDate: ServiceDate
  readonly toServiceDate: ServiceDate
  readonly workOrderIds: ReadonlyArray<WorkOrderId>
}

export type ChangeDateDailyStepValue = {
  readonly stopCount: number
  readonly batchCount: number
}

export type ChangeDateDailyStepResult = StepResult<'CHANGE_DATE_DAILY', ChangeDateDailyStepValue>

export const makeChangeDateDailyStep = (
  args: ChangeDateDailyStepArgs,
  deps: Deps,
): PipelineStep<'CHANGE_DATE_DAILY', ChangeDateDailyStepResult> => {
  const stepId = `change-date-daily:${args.routeId}:${args.fromServiceDate}->${args.toServiceDate}`
  return {
    id: stepId,
    phase: 'CHANGE_DATE_DAILY',
    label: 'Failed to flip service date on stops',
    applyOrder: 100,
    run: async (_ctx) => {
      if (args.workOrderIds.length === 0) {
        return ok({
          phase: 'CHANGE_DATE_DAILY',
          stepId,
          value: { stopCount: 0, batchCount: 0 },
        })
      }
      const batches = chunk(args.workOrderIds as readonly string[], BATCH_SIZE)
      let stopCount = 0
      try {
        for (const batch of batches) {
          const response: ChangeDateDailyResponse = await deps
            .changeDateDaily({
              routeId: args.routeId,
              data: {
                stops: [...batch],
                serviceDate: args.toServiceDate,
              },
              skipInvalidate: true,
            })
            .unwrap()
          stopCount += response?.stopCount ?? 0
        }
        return ok({
          phase: 'CHANGE_DATE_DAILY',
          stepId,
          value: { stopCount, batchCount: batches.length },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'CHANGE_DATE_DAILY',
          status,
          message,
        })
      }
    },
  }
}
